import { useEffect, useRef } from 'react'
import type { SetStateAction } from 'react'
import type { CanvasTool } from '../ProjectCanvas.types'
import { isInsideTaskDetails } from '../../../utils/taskDetailsFocus'

interface UseCanvasToolShortcutOptions {
  /** Nur bei offenem Canvas im sichtbaren Tab (TabStack-Falle). */
  enabled: boolean
  /**
   * Nimmt einen festen Wert ODER eine Aktualisierungsfunktion — `V` schaltet
   * um und braucht dafür das aktuelle Werkzeug (siehe unten). Verdrahtet ist
   * das mit dem `useState`-Setzer aus `ProjectCanvasPanel`, der beides kann.
   */
  onSelectTool: (tool: SetStateAction<CanvasTool>) => void
}

/**
 * `V` schaltet zwischen Maus- und Hand-Werkzeug UM, `H` greift direkt zur Hand
 * — die Kürzel aus Figma und Sketch, `V` zusätzlich als Umschalter.
 *
 * **Warum nur `V` umschaltet:** Der Buchstabe steht für die Sache. Schaltete
 * `H` aus der Hand heraus auf den Zeiger, bedeutete er mal Hand und mal Zeiger
 * — das kann sich niemand merken. So ist `V` die Umschalttaste und `H` der
 * direkte Griff: Wer aus der Hand zurück zum Zeiger will, drückt `V`; wer
 * sicher die Hand will, `H`.
 *
 * **Warum eine Aktualisierungsfunktion und kein durchgereichtes `tool`:** Der
 * Listener wird einmal gebunden und liest den Rückruf aus einem Ref. Käme das
 * aktuelle Werkzeug als Prop, müsste es entweder in die Abhängigkeiten (der
 * Listener bände sich bei jedem Werkzeugwechsel neu) oder in ein zweites Ref —
 * beides mehr Apparat für eine Auskunft, die React ohnehin liefert.
 *
 * Blanke Buchstaben, also mit denselben Vorsichten wie die Projekt-Kürzel
 * (A/B/F in `useProjectKeyboardShortcuts`): kein Modifikator, und nichts
 * davon greift, während in einem Textfeld getippt wird.
 *
 * @example
 * useCanvasToolShortcut({ enabled: isActiveTab && isOpen, onSelectTool: setTool })
 */
export function useCanvasToolShortcut({ enabled, onSelectTool }: UseCanvasToolShortcutOptions) {
  const onSelectToolRef = useRef(onSelectTool)
  onSelectToolRef.current = onSelectTool

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      // `code` statt `key`, damit die Kürzel auf jedem Tastaturlayout dort
      // liegen, wo sie eingezeichnet sind.
      if (e.code !== 'KeyV' && e.code !== 'KeyH') return
      // Im Text einer Notiz sind „v" und „h" schlicht Buchstaben.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Bei
      // geteiltem Canvas liegen sie als Slide-over darüber, und der Fokus steht
      // dort meist auf einem KNOPF — am INPUT/TEXTAREA-Wächter oben vorbei.
      if (isInsideTaskDetails(e.target)) return

      e.preventDefault()
      if (e.code === 'KeyV') {
        onSelectToolRef.current(current => (current === 'select' ? 'hand' : 'select'))
      } else {
        onSelectToolRef.current('hand')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled])
}
